import { useMemo } from 'react'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { getAllCards, getCardPriceHistory } from '@/services/api/cards'

const STALE_24H = 1000 * 60 * 60 * 24

const cardsQueries = {
  allList: {
    queryKey: () => ['cards', 'allList'] as const,
    queryFn: getAllCards,
    staleTime: STALE_24H,
    auth: 'public',
  } satisfies QueryDef<Awaited<ReturnType<typeof getAllCards>>>,
  priceHistory: {
    queryKey: (id: number, days: number) => ['cards', 'priceHistory', id, days] as const,
    queryFn: (id: number, days: number) => getCardPriceHistory(id, days),
    staleTime: STALE_24H,
    auth: 'public',
  } satisfies QueryDef<Awaited<ReturnType<typeof getCardPriceHistory>>, [number, number]>,
}

// Exported for usePrefetchBulkData.ts cross-file key reference
export { cardsQueries }

export function useCards() {
  const query = useDefinedQuery(cardsQueries.allList)
  return {
    ...query,
    data: query.data?.cards,
  }
}

export function useCard(id: number) {
  const { data: allCards, isLoading, error } = useCards()

  const indexes = useMemo(() => {
    if (!allCards) return null
    const byId = new Map<number, (typeof allCards)[number]>()
    const byImageId = new Map<string, typeof allCards>()
    for (const c of allCards) {
      byId.set(c.id, c)
      let group = byImageId.get(c.cardImageId)
      if (!group) {
        group = []
        byImageId.set(c.cardImageId, group)
      }
      group.push(c)
    }
    return { byId, byImageId }
  }, [allCards])

  const data = useMemo(() => {
    if (!indexes) return undefined
    const card = indexes.byId.get(id)
    if (!card) return undefined
    const variants = (indexes.byImageId.get(card.cardImageId) ?? []).filter((v) => v.id !== id)
    return { ...card, variants }
  }, [indexes, id])

  return { data, isLoading, error }
}

export function useCardPriceHistory(id: number | undefined, days: number = 30) {
  // `?? 0` is a placeholder key segment while disabled — the query never runs without an id.
  return useDefinedQuery(cardsQueries.priceHistory, id ?? 0, days, { enabled: !!id })
}
